import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth/AuthContext";
import { Logo } from "@/components/intel/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Shield } from "lucide-react";

export const Route = createFileRoute("/admin-login")({
  head: () => ({ meta: [{ title: "Administrator · CARIO INTEL" }] }),
  component: AdminLoginPage,
});

function AdminLoginPage() {
  const { session, isAdmin, loading, signIn, signOut } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (session && isAdmin) navigate({ to: "/admin" });
  }, [loading, session, isAdmin, navigate]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await signIn(email, password);
      // Wait a tick for role to load — RequireAuth on /admin will gate.
      setTimeout(() => navigate({ to: "/admin" }), 200);
    } catch (e: any) {
      toast.error(e?.message || "Authentication failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen grid-bg bg-background text-foreground flex items-center justify-center px-4">
      <Toaster />
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <Link to="/login" className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan hover:text-foreground">
            Operator sign in →
          </Link>
        </div>
        <div className="glass-strong rounded-xl p-8 ring-glow border border-cyan/30">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan flex items-center gap-2">
            <Shield className="h-3 w-3" /> // Administrator console
          </p>
          <h1 className="mt-2 font-display text-3xl">Privileged access</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Restricted to platform administrators.
          </p>

          {session && !isAdmin && !loading && (
            <div className="mt-6 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs">
              You are signed in as a non-admin operator.{" "}
              <button className="underline" onClick={() => signOut()}>Sign out</button> and try again.
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Admin email</Label>
              <Input id="email" type="email" autoComplete="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@org.com" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" autoComplete="current-password" required minLength={8}
                value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            <Button type="submit" disabled={busy} className="w-full">
              {busy ? "Working…" : "Enter control room"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}